import React from 'react';
import { connect } from 'react-redux';
import { Dropdown, DropdownProps, DropdownItemProps } from 'semantic-ui-react';
import { createStructuredSelector } from 'reselect';

import { AppState } from 'services/types';
import {
    VirtualizedTableErrorMessage,
    VirtualizedTableProps,
    VirtualizedTableItemValue
} from 'services/virtualizedTable/types';
import { setItemValue } from 'services/virtualizedTable/actions';
import {
    makeSelectVirtualizedTableColumnOptions,
    makeSelectVirtualizedTableItemValue,
    makeSelectVirtualizedTableErrorMessage
} from 'services/virtualizedTable/selectors';

import { VirtualizedTableOnItemValueChange } from '../../types';
import './styles.scss';

type Value = string | number | undefined | null;

interface OwnProps extends VirtualizedTableProps {
    isOpen?: boolean;
    onOpen: Function;
    onClose: Function;
    isFocused?: boolean;
    onItemValueChange?: VirtualizedTableOnItemValueChange;
}

interface ConnectedProps {
    itemValue?: VirtualizedTableItemValue;
    options?: DropdownItemProps[] | undefined;
    errorMessage?: VirtualizedTableErrorMessage;
}

interface DispatchProps {
    setItemValue: typeof setItemValue;
}

type Props = OwnProps & ConnectedProps & DispatchProps;

interface State {
    isFocused: boolean;
}

export class VirtualizedTableDropdownComponent extends React.PureComponent<
    Props,
    State
> {
    textInput = React.createRef<React.Component<DropdownProps>>();

    state = {
        isFocused: false
    };

    componentDidMount(): void {
        const { isFocused } = this.props;

        /**
         * Prevent the element being scrolled to when it is rendered to the window.
         * We only focus on mount when we know that the user has clicked on the parent cell.
         */
        if (isFocused) {
            this.forceFocus();
        }
        document.addEventListener('keydown', this.handleKeyPress);
    }

    componentDidUpdate(prevProps: Props): void {
        const { props } = this;
        const { isFocused } = this.state;

        if (!isFocused && !prevProps.isOpen && props.isOpen) {
            this.forceFocus();
        }
    }

    componentWillUnmount(): void {
        document.removeEventListener('keydown', this.handleKeyPress);
    }

    /**
     * Force input foucus when the cell is active and the user starts typing.
     */
    handleKeyPress = (event: KeyboardEvent): void => {
        const { isFocused } = this.state;

        if (!isFocused) {
            this.forceFocus();
        }
    };

    forceFocus = (): void => {
        this.setState({ isFocused: true }, () => {
            const { current } = this.textInput;
            if (current) {
                // @ts-ignore
                current.handleFocus({
                    preventScroll: true
                });
            }
        });
    };

    handleChange = (
        event: React.SyntheticEvent<HTMLElement, Event>,
        data: DropdownProps
    ): void => {
        const {
            itemKey,
            columnKey,
            onClose,
            onItemValueChange,
            ...props
        } = this.props;
        const { value, options = [] } = data;
        const selectedOption = options.find(option => option.value === value);

        if (itemKey && columnKey && selectedOption) {
            props.setItemValue(itemKey, columnKey, value);

            if (onItemValueChange) {
                onItemValueChange(itemKey, columnKey, value);
            }
        }
    };

    handleClose = (): void => {
        const { onClose } = this.props;
        onClose();
    };

    handleClick = (): void => {
        const { onOpen } = this.props;
        onOpen();
    };

    handleSearchChange = (): void => {
        const { onOpen } = this.props;
        onOpen();
    };

    handleBlur = (): void => {
        this.setState({ isFocused: false });
    };

    render(): React.ReactElement {
        const { itemValue, options = [] } = this.props;

        return (
            <Dropdown
                ref={this.textInput}
                className="entity-value-update-dropdown"
                fluid
                search
                icon={null}
                scrolling
                floating
                value={itemValue !== null ? itemValue : undefined}
                options={options}
                onChange={this.handleChange}
                onClose={this.handleClose}
                onClick={this.handleClick}
                onBlur={this.handleBlur}
                closeOnBlur={false}
                onSearchChange={this.handleSearchChange}
            />
        );
    }
}

const mapStateToProps = createStructuredSelector<
    AppState,
    OwnProps,
    ConnectedProps
>({
    itemValue: makeSelectVirtualizedTableItemValue(),
    options: makeSelectVirtualizedTableColumnOptions(),
    errorMessage: makeSelectVirtualizedTableErrorMessage()
});

export default connect(
    mapStateToProps,
    { setItemValue }
)(VirtualizedTableDropdownComponent);
